/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { routes } from '@/route-paths';

/** Every footer:* translation key referenced by FOOTER_NAV_COLUMNS, kept literal so t() stays type-checked. */
export type FooterNavLabelKey =
    | 'sections.account'
    | 'sections.customerSupport'
    | 'sections.about'
    | 'sections.contactUs'
    | 'links.signInOrCreateAccount'
    | 'links.myAccount'
    | 'links.wishlist'
    | 'links.shipping'
    | 'links.orderStatus'
    | 'links.aboutUs'
    | 'links.accessibility';

export interface FooterNavLink {
    labelKey: FooterNavLabelKey;
    to: string;
}

export interface FooterNavColumn {
    id: 'account' | 'customerSupport' | 'about' | 'contactUs';
    labelKey: FooterNavLabelKey;
    links?: FooterNavLink[];
    /** Contact Us renders a support phone number + payment badges instead of a link list. */
    isContactColumn?: boolean;
}

/**
 * Single source of truth for the footer nav-link matrix, rendered both as a
 * 4-column desktop grid and a mobile accordion — see nav-columns.tsx.
 */
export const FOOTER_NAV_COLUMNS: FooterNavColumn[] = [
    {
        id: 'account',
        labelKey: 'sections.account',
        links: [
            { labelKey: 'links.signInOrCreateAccount', to: routes.login },
            { labelKey: 'links.myAccount', to: routes.account },
            { labelKey: 'links.wishlist', to: routes.accountWishlist },
        ],
    },
    {
        id: 'customerSupport',
        labelKey: 'sections.customerSupport',
        links: [
            // No dedicated shipping-info page exists yet; placeholder pending content.
            { labelKey: 'links.shipping', to: '#' },
            { labelKey: 'links.orderStatus', to: routes.accountOrders },
        ],
    },
    {
        id: 'about',
        labelKey: 'sections.about',
        links: [
            { labelKey: 'links.aboutUs', to: '/about-us' },
            { labelKey: 'links.accessibility', to: '/accessibility' },
        ],
    },
    {
        id: 'contactUs',
        labelKey: 'sections.contactUs',
        isContactColumn: true,
    },
];
